// Assignment Completion Policies (ADR-095)
//
// How many sequential and how many parallel assignments an individual in a
// Cluster is expected to go through in a year. The Assignment
// Completion report reads these; a cluster with no policy expects nothing and
// shows "—" there rather than 0%.
//
// Every Cluster is listed, configured or not: an empty row is
// something to fill in, whereas an absent one is something you have to
// notice.
//
// ADR-096: each configured cluster expands to its per-Position overrides. The add
// control is absent until the generic pair is saved — an override cannot
// exist without the policy it modifies, and a form that cannot be submitted
// is worse than one that is not offered.
//
// ADR-097 adds the middle layer: a rule for a whole POSITION TYPE, rendered as
// the group heading its position overrides sit under. Groups are DERIVED from
// the positions and rules present — there is no folder to create and none to
// clean up, so the tree cannot disagree with its contents. The wording says
// "position type", never "Organizational Unit Type": the report's filter bar
// already uses those words for the BUSINESS UNIT's type, which is a different
// dimension (ADR-097 §3-4).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaChevronRight, FaChevronDown, FaTimes } from 'react-icons/fa';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
// Shared page shell — the same source Project Type Policies uses. A config
// page has no business borrowing the reports module's styles.
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../../common/styles';
import {
  FiltersBar,
  GridHeader,
  PolicyRow,
  OverrideRow,
  OverrideBlock,
  AddArea,
  Actions,
  Hint,
} from './styles';
import PositionSelector from '../../common/PositionSelector';
import OrgUnitTypeSelector from '../../common/OrgUnitTypeSelector';
import { POSITION_RETURN_TYPES, ORG_UNIT_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { assignmentCompletionApi } from '../../../services/api/assignmentCompletionApi';

/**
 * Parents before their children, each child directly under its parent, with a
 * depth so the page can indent them.
 *
 * Clusters are a TREE (ADR-095 §3: a cluster with no policy of its own
 * inherits its parent's), and this page used to render them as a flat list —
 * so setting the root's pair silently configured every region under it and
 * the page still labelled those regions "No policy". The hierarchy is the
 * missing context, not a decoration.
 */
const orderByHierarchy = (entries) => {
  const byParent = new Map();
  const known = new Set(entries.map((entry) => entry.cluster.id));

  for (const entry of entries) {
    // A parent outside this list cannot be nested under anything here, so the
    // entry is a root as far as this page is concerned.
    const parentId = entry.cluster.parentClusterId && known.has(entry.cluster.parentClusterId)
      ? entry.cluster.parentClusterId
      : null;
    if (!byParent.has(parentId)) {
      byParent.set(parentId, []);
    }
    byParent.get(parentId).push(entry);
  }

  const ordered = [];
  const walk = (parentId, depth) => {
    for (const entry of byParent.get(parentId) || []) {
      ordered.push({ ...entry, depth });
      walk(entry.cluster.id, depth + 1);
    }
  };
  walk(null, 0);

  // Anything unreachable (a parent cycle, say) still has to appear — a
  // cluster missing from a configuration page is worse than one shown
  // at the wrong indent.
  if (ordered.length < entries.length) {
    const shown = new Set(ordered.map((entry) => entry.cluster.id));
    for (const entry of entries) {
      if (!shown.has(entry.cluster.id)) {
        ordered.push({ ...entry, depth: 0 });
      }
    }
  }
  return ordered;
};

/** Blank means "no policy" — distinct from a configured 0. */
const toInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const AssignmentCompletionPolicies = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [savingClusterId, setSavingClusterId] = useState(null);
  // ADR-096: which clusters are expanded, and the in-progress override being added
  // per cluster. Keyed by cluster so two rows can be edited without interfering.
  const [expandedClusterIds, setExpandedClusterIds] = useState(() => new Set());
  const [overrideDrafts, setOverrideDrafts] = useState({});
  // ADR-097: the in-progress position-TYPE rule per cluster
  const [typeDrafts, setTypeDrafts] = useState({});

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 3; y--) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  const load = useCallback(async (targetYear) => {
    try {
      setIsLoading(true);
      const data = await assignmentCompletionApi.getPolicies(targetYear);
      setRows(orderByHierarchy(data.deliveryCenters || []));
      // Drafts mirror what is on screen; blank inputs mean "no policy yet".
      const nextDrafts = {};
      for (const entry of data.deliveryCenters || []) {
        nextDrafts[entry.cluster.id] = {
          sequential: toInputValue(entry.policy?.sequentialPerIndividual),
          parallel: toInputValue(entry.policy?.parallelPerIndividual),
        };
      }
      setDrafts(nextDrafts);
    } catch (error) {
      showAlert('Failed to load assignment completion policies', 'error', error);
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    load(year);
  }, [load, year]);

  const setDraft = (clusterId, field, value) => {
    // Digits only; empty stays empty so a row can be left unconfigured.
    if (value !== '' && !/^\d{0,3}$/.test(value)) {
      return;
    }
    setDrafts((prev) => ({
      ...prev,
      [clusterId]: { ...prev[clusterId], [field]: value },
    }));
  };

  const handleSave = async (entry) => {
    const draft = drafts[entry.cluster.id] || {};
    const sequential = parseInt(draft.sequential, 10);
    const parallel = parseInt(draft.parallel, 10);

    if (Number.isNaN(sequential) || Number.isNaN(parallel)) {
      showAlert('Enter both a sequential and a parallel count (0 is allowed)', 'warning');
      return;
    }

    try {
      setSavingClusterId(entry.cluster.id);
      await assignmentCompletionApi.savePolicy({
        clusterId: entry.cluster.id,
        year,
        sequentialPerIndividual: sequential,
        parallelPerIndividual: parallel,
      });
      showAlert(`Policy saved for ${entry.cluster.name}`, 'success');
      await load(year);
    } catch (error) {
      showAlert('Failed to save policy', 'error', error);
    } finally {
      setSavingClusterId(null);
    }
  };

  const handleClear = async (entry) => {
    if (!entry.policy) {
      return;
    }
    try {
      setSavingClusterId(entry.cluster.id);
      await assignmentCompletionApi.deletePolicy(entry.policy.id);
      showAlert(`${entry.cluster.name} now has no policy for ${year}`, 'success');
      await load(year);
    } catch (error) {
      showAlert('Failed to remove policy', 'error', error);
    } finally {
      setSavingClusterId(null);
    }
  };

  // ── Position overrides (ADR-096) ────────────────────────────────────────

  const toggleExpanded = (clusterId) => {
    setExpandedClusterIds((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) {
        next.delete(clusterId);
      } else {
        next.add(clusterId);
      }
      return next;
    });
  };

  const setOverrideDraft = (clusterId, field, value) => {
    if (field !== 'positionId' && value !== '' && !/^\d{0,3}$/.test(value)) {
      return;
    }
    setOverrideDrafts((prev) => ({
      ...prev,
      [clusterId]: { positionId: '', sequential: '', parallel: '', ...prev[clusterId], [field]: value },
    }));
  };

  const handleAddOverride = async (entry) => {
    const draft = overrideDrafts[entry.cluster.id] || {};
    const sequential = parseInt(draft.sequential, 10);
    const parallel = parseInt(draft.parallel, 10);

    if (!draft.positionId) {
      showAlert('Choose a position to override', 'warning');
      return;
    }
    // Both numbers, always: an override is a full replacement pair, never a
    // patch on the generic one (ADR-096 §2). "Only 1 parallel" is 0 + 1.
    if (Number.isNaN(sequential) || Number.isNaN(parallel)) {
      showAlert('Enter both counts for the position (0 is allowed)', 'warning');
      return;
    }

    try {
      setSavingClusterId(entry.cluster.id);
      await assignmentCompletionApi.savePositionPolicy({
        policyId: entry.policy.id,
        positionId: draft.positionId,
        sequentialPerIndividual: sequential,
        parallelPerIndividual: parallel,
      });
      setOverrideDrafts((prev) => ({ ...prev, [entry.cluster.id]: { positionId: '', sequential: '', parallel: '' } }));
      showAlert(`Position override saved for ${entry.cluster.name}`, 'success');
      await load(year);
    } catch (error) {
      showAlert('Failed to save position override', 'error', error);
    } finally {
      setSavingClusterId(null);
    }
  };

  const setTypeDraft = (clusterId, field, value) => {
    if (field !== 'orgUnitTypeId' && value !== '' && !/^\d{0,3}$/.test(value)) {
      return;
    }
    setTypeDrafts((prev) => ({
      ...prev,
      [clusterId]: { orgUnitTypeId: '', sequential: '', parallel: '', ...prev[clusterId], [field]: value },
    }));
  };

  const handleAddTypeRule = async (entry) => {
    const draft = typeDrafts[entry.cluster.id] || {};
    const sequential = parseInt(draft.sequential, 10);
    const parallel = parseInt(draft.parallel, 10);

    if (!draft.orgUnitTypeId) {
      showAlert('Choose a position type', 'warning');
      return;
    }
    if (Number.isNaN(sequential) || Number.isNaN(parallel)) {
      showAlert('Enter both counts for the position type (0 is allowed)', 'warning');
      return;
    }

    try {
      setSavingClusterId(entry.cluster.id);
      await assignmentCompletionApi.savePositionTypePolicy({
        policyId: entry.policy.id,
        orgUnitTypeId: draft.orgUnitTypeId,
        sequentialPerIndividual: sequential,
        parallelPerIndividual: parallel,
      });
      setTypeDrafts((prev) => ({ ...prev, [entry.cluster.id]: { orgUnitTypeId: '', sequential: '', parallel: '' } }));
      showAlert(`Position type rule saved for ${entry.cluster.name}`, 'success');
      await load(year);
    } catch (error) {
      showAlert('Failed to save position type rule', 'error', error);
    } finally {
      setSavingClusterId(null);
    }
  };

  const handleRemoveTypeRule = async (entry, rule) => {
    try {
      setSavingClusterId(entry.cluster.id);
      await assignmentCompletionApi.deletePositionTypePolicy({
        policyId: entry.policy.id,
        orgUnitTypeId: rule.orgUnitTypeId,
      });
      showAlert(
        `${rule.orgUnitType?.name || 'That type'}'s positions now follow ${entry.cluster.name}'s general rule`,
        'success',
      );
      await load(year);
    } catch (error) {
      showAlert('Failed to remove position type rule', 'error', error);
    } finally {
      setSavingClusterId(null);
    }
  };

  const handleRemoveOverride = async (entry, override) => {
    try {
      setSavingClusterId(entry.cluster.id);
      await assignmentCompletionApi.deletePositionPolicy({
        policyId: entry.policy.id,
        positionId: override.positionId,
      });
      showAlert(
        `${override.position?.label || 'Position'} now follows ${entry.cluster.name}'s general rule`,
        'success',
      );
      await load(year);
    } catch (error) {
      showAlert('Failed to remove position override', 'error', error);
    } finally {
      setSavingClusterId(null);
    }
  };

  // "CORPORATE POSITIONS" — but never by blind concatenation. A group built
  // from a position override carries its type's real name; a position with no
  // type has no name to use, so it gets a heading that reads as English
  // instead of "NO POSITION TYPE POSITIONS".
  // An inheriting cluster shows the numbers it is ACTUALLY measured against,
  // as placeholder text — typing over them is what "override" means here, and
  // an em dash would repeat the lie that nothing applies.
  const inheritedPlaceholder = (entry, field) => (entry.inheritedFrom && entry.effectivePolicy
    ? String(entry.effectivePolicy[field] ?? 0)
    : '—');

  const inheritedTotal = (entry) => (entry.inheritedFrom && entry.effectivePolicy
    ? (entry.effectivePolicy.sequentialPerIndividual || 0) + (entry.effectivePolicy.parallelPerIndividual || 0)
    : '—');

  const groupHeading = (group) => (group.name
    ? `${group.name.toUpperCase()} POSITIONS`
    : 'POSITIONS WITH NO TYPE');

  // BOTH kinds of exception override the cluster's general rule
  // (ADR-096 §1, ADR-097 §1), so both are counted the same way everywhere they
  // are named. Counting only positions made a cluster configured entirely by type
  // rules read as having no exceptions at all, while the Clear warning on the
  // same row already said it had one.
  const exceptionParts = (typeCount, positionCount) => {
    const parts = [];
    if (typeCount > 0) {
      parts.push(`${typeCount} position type rule${typeCount === 1 ? '' : 's'}`);
    }
    if (positionCount > 0) {
      parts.push(`${positionCount} position override${positionCount === 1 ? '' : 's'}`);
    }
    return parts;
  };

  // Clear cascades to BOTH kinds of exception, and a cascade nobody was told
  // about is one they find out about afterwards.
  const clearWarning = (typeCount, positionCount) => {
    const parts = exceptionParts(typeCount, positionCount);
    const tail = 'this cluster will expect nothing for this year.';
    return parts.length > 0
      ? `Remove the policy and its ${parts.join(' and ')} — ${tail}`
      : `Remove the policy — ${tail}`;
  };

  const configuredCount = rows.filter((entry) => entry.policy).length;
  // Counted separately: an inheriting cluster is neither "configured" nor the
  // gap that word implies.
  const inheritingCount = rows.filter((entry) => !entry.policy && entry.inheritedFrom).length;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          {/* Home, not Feedback Sessions: this is a configuration page
              reached from the navigation menu, exactly like Project Type
              Policies — its parent in the menu is not where the user came
              from. */}
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Assignment Completion Policies</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          <FiltersBar>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {yearOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Hint>
              {configuredCount} of {rows.length} clusters configured for {year}
              {inheritingCount > 0 && `, ${inheritingCount} inheriting`}
            </Hint>
          </FiltersBar>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <GridHeader>
                <span>Cluster</span>
                <Tooltip title="Sequential assignments expected of each individual in this cluster during the year.">
                  <span className="num help">Sequential</span>
                </Tooltip>
                <Tooltip title="Parallel assignments expected of each individual. Work of one type never counts toward the other.">
                  <span className="num help">Parallel</span>
                </Tooltip>
                <Tooltip title="What one individual is expected to complete in total this year.">
                  <span className="num help">Total per individual</span>
                </Tooltip>
                <span />
              </GridHeader>

              {rows.map((entry) => {
                const draft = drafts[entry.cluster.id] || { sequential: '', parallel: '' };
                const sequential = parseInt(draft.sequential, 10);
                const parallel = parseInt(draft.parallel, 10);
                const total = (Number.isNaN(sequential) ? 0 : sequential)
                  + (Number.isNaN(parallel) ? 0 : parallel);
                const isBlank = draft.sequential === '' && draft.parallel === '';
                const isDirty = entry.policy
                  ? String(entry.policy.sequentialPerIndividual) !== draft.sequential
                    || String(entry.policy.parallelPerIndividual) !== draft.parallel
                  : !isBlank;
                const isBusy = savingClusterId === entry.cluster.id;
                const overrides = entry.policy?.positionPolicies || [];
                const isExpanded = expandedClusterIds.has(entry.cluster.id);
                const overrideDraft = overrideDrafts[entry.cluster.id]
                  || { positionId: '', sequential: '', parallel: '' };
                // Already-overridden positions are removed from the picker, so
                // the unique constraint is a backstop rather than an error
                // message someone has to read.
                const takenPositionIds = overrides.map((o) => o.positionId);

                // ADR-097: the tree, DERIVED. A group exists when it has a
                // type rule OR any position override under it — never because
                // someone created a folder, so it can never be empty and can
                // never disagree with the positions in it.
                const typeRules = entry.policy?.orgUnitTypePolicies || [];
                const takenTypeIds = typeRules.map((rule) => rule.orgUnitTypeId);
                const groups = new Map();
                for (const rule of typeRules) {
                  groups.set(rule.orgUnitTypeId, {
                    id: rule.orgUnitTypeId,
                    name: rule.orgUnitType?.name || null,
                    rule,
                    positions: [],
                  });
                }
                for (const override of overrides) {
                  const typeId = override.position?.orgUnitTypeId || null;
                  if (!groups.has(typeId)) {
                    groups.set(typeId, {
                      id: typeId,
                      // The type's own name, sent with the override — never a
                      // placeholder. A position with NO type gets no name at
                      // all and is headed differently (see groupHeading).
                      name: override.position?.orgUnitType?.name || null,
                      rule: null,
                      positions: [],
                    });
                  }
                  groups.get(typeId).positions.push(override);
                }
                const groupList = [...groups.values()];
                // One count for the row's tag: a type rule and a position
                // override are both exceptions to the general rule, and the
                // tag must agree with the Clear warning beside it.
                const exceptionCount = typeRules.length + overrides.length;

                return (
                  <React.Fragment key={entry.cluster.id}>
                  <PolicyRow $unconfigured={!entry.policy && !entry.inheritedFrom} $depth={entry.depth || 0}>
                    <div className="unit">
                      {/* Only a configured cluster can hold overrides, so only a
                          configured cluster is expandable (ADR-096 §5). */}
                      {entry.policy ? (
                        <span
                          className="chevron"
                          onClick={() => toggleExpanded(entry.cluster.id)}
                        >
                          {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                      ) : (
                        <span className="chevron placeholder" />
                      )}
                      {entry.cluster.name}
                      {/* ADR-119: Organizational Structure Type as a muted code
                          (tooltip = full name), as the selectors render it. */}
                      {entry.cluster.orgStructureType?.code && (
                        <Tooltip title={entry.cluster.orgStructureType.name}>
                          <span style={{ marginLeft: 6, color: '#6B7280', fontFamily: 'monospace', fontSize: '12px' }}>
                            {entry.cluster.orgStructureType.code}
                          </span>
                        </Tooltip>
                      )}
                      {exceptionCount > 0 && (
                        <Tooltip title={`This cluster's general rule is overridden by ${exceptionParts(typeRules.length, overrides.length).join(' and ')}.`}>
                          <span className="tag overrides">
                            {exceptionCount} override{exceptionCount === 1 ? '' : 's'}
                          </span>
                        </Tooltip>
                      )}
                      {/* "No policy" is only true when NOTHING resolves. A
                          cluster inheriting its parent's is fully measured,
                          and labelling that "No policy" is what hid a
                          company-wide default from the person who set it. */}
                      {!entry.policy && entry.inheritedFrom && (
                        <Tooltip title={`This cluster has no policy of its own, so it is measured against ${entry.inheritedFrom.name}'s — including that policy's position rules. Enter its own numbers to override.`}>
                          <span className="tag inherited">
                            inherits from {entry.inheritedFrom.name}
                          </span>
                        </Tooltip>
                      )}
                      {!entry.policy && !entry.inheritedFrom && (
                        <Tooltip title="No policy for this year — the report expects nothing of this cluster and shows “—” instead of a percentage.">
                          <span className="tag">No policy</span>
                        </Tooltip>
                      )}
                    </div>

                    <TextField
                      className="count"
                      size="small"
                      value={draft.sequential}
                      onChange={(e) => setDraft(entry.cluster.id, 'sequential', e.target.value)}
                      inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                      placeholder={inheritedPlaceholder(entry, 'sequentialPerIndividual')}
                    />
                    <TextField
                      className="count"
                      size="small"
                      value={draft.parallel}
                      onChange={(e) => setDraft(entry.cluster.id, 'parallel', e.target.value)}
                      inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                      placeholder={inheritedPlaceholder(entry, 'parallelPerIndividual')}
                    />

                    <div className={`total${isBlank ? ' muted' : ''}`}>
                      {isBlank ? inheritedTotal(entry) : total}
                    </div>

                    <Actions>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!isDirty || isBusy}
                        onClick={() => handleSave(entry)}
                      >
                        {isBusy ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                      {entry.policy && (
                        <Tooltip title={clearWarning(typeRules.length, overrides.length)}>
                          <span>
                            <Button
                              size="small"
                              color="inherit"
                              disabled={isBusy}
                              onClick={() => handleClear(entry)}
                            >
                              Clear
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Actions>
                  </PolicyRow>

                  {entry.policy && isExpanded && (
                    <OverrideBlock $depth={entry.depth || 0}>
                      {groupList.length === 0 && (
                        <div className="empty">
                          Every individual in {entry.cluster.name} is expected to complete the
                          same work. Add a position type or a single position below to give it
                          its own numbers.
                        </div>
                      )}

                      {groupList.map((group) => (
                        <React.Fragment key={group.id || 'untyped'}>
                          {/* The group heading IS the type rule when there is
                              one. Where there is not, it says so — a silent
                              heading would look like a rule that is not there. */}
                          <OverrideRow className="group">
                            <div className="position type">
                              {groupHeading(group)}
                            </div>
                            {group.rule ? (
                              <>
                                <div className="num">{group.rule.sequentialPerIndividual}</div>
                                <div className="num">{group.rule.parallelPerIndividual}</div>
                                <div className="num total">
                                  {group.rule.sequentialPerIndividual + group.rule.parallelPerIndividual}
                                </div>
                                <Tooltip title={`Remove — positions of this type fall back to ${entry.cluster.name}'s general rule, except any with an override of their own.`}>
                                  <span>
                                    <Button
                                      size="small"
                                      color="inherit"
                                      disabled={isBusy}
                                      onClick={() => handleRemoveTypeRule(entry, group.rule)}
                                    >
                                      <FaTimes />
                                    </Button>
                                  </span>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <div className="num muted follows">
                                  follows {entry.cluster.name}
                                </div>
                                <div />
                                <div />
                                <div />
                              </>
                            )}
                          </OverrideRow>

                          {group.positions.map((override) => (
                            <OverrideRow key={override.positionId}>
                              <div className="position nested">{override.position?.label || 'Position'}</div>
                              <div className="num">{override.sequentialPerIndividual}</div>
                              <div className="num">{override.parallelPerIndividual}</div>
                              <div className="num total">
                                {override.sequentialPerIndividual + override.parallelPerIndividual}
                              </div>
                              <Tooltip title={group.rule
                                ? `Remove — this position falls back to the ${group.name} rule above. Different from setting it to 0 + 0.`
                                : `Remove — this position falls back to ${entry.cluster.name}'s general rule. Different from setting it to 0 + 0.`}
                              >
                                <span>
                                  <Button
                                    size="small"
                                    color="inherit"
                                    disabled={isBusy}
                                    onClick={() => handleRemoveOverride(entry, override)}
                                  >
                                    <FaTimes />
                                  </Button>
                                </span>
                              </Tooltip>
                            </OverrideRow>
                          ))}
                        </React.Fragment>
                      ))}

                      {/* Everything above this line is saved. Everything below
                          it is a form — see AddArea in styles. */}
                      <AddArea>
                      <div className="caption">Add an exception to {entry.cluster.name}</div>
                      <OverrideRow className="add">
                        <div className="position">
                          <OrgUnitTypeSelector
                            label="Add position type"
                            value={typeDrafts[entry.cluster.id]?.orgUnitTypeId || ''}
                            returnValue={ORG_UNIT_TYPE_RETURN_TYPES.ID}
                            onChange={(value) => setTypeDraft(entry.cluster.id, 'orgUnitTypeId', value)}
                            excludeIds={takenTypeIds}
                            size="small"
                          />
                        </div>
                        <TextField
                          className="count"
                          size="small"
                          value={typeDrafts[entry.cluster.id]?.sequential || ''}
                          onChange={(e) => setTypeDraft(entry.cluster.id, 'sequential', e.target.value)}
                          inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                          placeholder="—"
                        />
                        <TextField
                          className="count"
                          size="small"
                          value={typeDrafts[entry.cluster.id]?.parallel || ''}
                          onChange={(e) => setTypeDraft(entry.cluster.id, 'parallel', e.target.value)}
                          inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                          placeholder="—"
                        />
                        <div className="num total muted">—</div>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={isBusy || !typeDrafts[entry.cluster.id]?.orgUnitTypeId}
                          onClick={() => handleAddTypeRule(entry)}
                        >
                          Add
                        </Button>
                      </OverrideRow>

                      <OverrideRow className="add">
                        <div className="position">
                          <PositionSelector
                            label="Add position"
                            value={overrideDraft.positionId}
                            returnValue={POSITION_RETURN_TYPES.ID}
                            onChange={(value) => setOverrideDraft(entry.cluster.id, 'positionId', value)}
                            excludeIds={takenPositionIds}
                            size="small"
                          />
                          {/* Positions keep their type grouping in the picker,
                              so a position added here lands in the right group
                              automatically — no folder to pick first. */}
                        </div>
                        <TextField
                          className="count"
                          size="small"
                          value={overrideDraft.sequential}
                          onChange={(e) => setOverrideDraft(entry.cluster.id, 'sequential', e.target.value)}
                          inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                          placeholder="—"
                        />
                        <TextField
                          className="count"
                          size="small"
                          value={overrideDraft.parallel}
                          onChange={(e) => setOverrideDraft(entry.cluster.id, 'parallel', e.target.value)}
                          inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                          placeholder="—"
                        />
                        <div className="num total muted">—</div>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={isBusy || !overrideDraft.positionId}
                          onClick={() => handleAddOverride(entry)}
                        >
                          Add
                        </Button>
                      </OverrideRow>
                      </AddArea>
                    </OverrideBlock>
                  )}
                  </React.Fragment>
                );
              })}

              <Hint className="footer">
                A cluster with no policy is not measured: its business units show “—” for
                Expected and % Completed on the Assignment Completion report. A configured 0 is
                different — it says this year expects nothing. A position type rule replaces both of a
                cluster's numbers for everyone holding a position of that type, and a
                position override replaces them again for that one position — most specific
                wins. People are measured against the position they held during the year.
              </Hint>
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default AssignmentCompletionPolicies;
